export const hexToRgb = (hex: string): [number, number, number] | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? [parseInt(result[1] ?? '0', 16), parseInt(result[2] ?? '0', 16), parseInt(result[3] ?? '0', 16)] : null
}

const hue_FromString = (name: string, avalanche = true): number => {
    let hash = 0x811c9dc5
    for (let i = 0; i < name.length; i++) {
        hash ^= name.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193)
    }
    if (avalanche) {
        hash ^= hash >>> 16
        hash = Math.imul(hash, 0x45d9f3b)
        hash ^= hash >>> 16
    }
    return ((hash % 360) + 360) % 360
}

export type LCHColor = `oklch(${string})` | `oklch(${string} / ${number}) `

export const stringToLchColor = (name: string, chroma: number, luminance: number, avalanche = true): LCHColor =>
    `oklch(${luminance}% ${chroma}% ${hue_FromString(name, avalanche)})`
export const lchSetOpacity = (lchColor: LCHColor, alpha: number): LCHColor => lchColor.replace(')', ` / ${alpha})`) as LCHColor

export const stringToLCH = (name: string, chroma: number, luminance: number, avalanche = true) =>
    [luminance, chroma, hue_FromString(name, avalanche)] as [number, number, number]

export const getTextColorByContrast = (bgColor: [number, number, number]): string => (bgColor[0] < 75 ? '#fffff2' : '#464b53')

/**
 * Parse RGB/RGBA color string to get RGB values
 */
export const parseRgb = (rgbString: string): [number, number, number] | null => {
    const match = rgbString.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (!match || !match[1] || !match[2] || !match[3]) return null
    return [parseInt(match[1]) / 255, parseInt(match[2]) / 255, parseInt(match[3]) / 255]
}

/**
 * Calculate relative luminance of a color to determine if it's light or dark
 * Returns true if the color is light (needs dark text)
 */
export const isLightColor = (color: string): boolean => {
    let r: number, g: number, b: number

    if (color.startsWith('#')) {
        const hex = color.replace('#', '')
        let normalizedHex: string

        if (hex.length === 3) {
            normalizedHex = hex
                .split('')
                .map(ch => ch + ch)
                .join('')
        } else if (hex.length === 6) {
            normalizedHex = hex
        } else {
            return false
        }

        r = parseInt(normalizedHex.substring(0, 2), 16) / 255
        g = parseInt(normalizedHex.substring(2, 4), 16) / 255
        b = parseInt(normalizedHex.substring(4, 6), 16) / 255
    } else if (color.startsWith('rgb')) {
        const rgb = parseRgb(color)
        if (!rgb) return false
        r = rgb[0]
        g = rgb[1]
        b = rgb[2]
    } else if (color.startsWith('oklch')) {
        const lMatch = color.match(/oklch\(\s*([\d.]+)%/)
        const alphaMatch = color.match(/\/\s*([\d.]+)\s*\)/)
        if (!lMatch?.[1]) return false
        const l = parseFloat(lMatch[1]) / 100
        const alpha = alphaMatch?.[1] ? parseFloat(alphaMatch[1]) : 1
        return l * alpha + 1 * (1 - alpha) > 0.5
    } else {
        return false
    }

    const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
    const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
    const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

    const luminance = 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB

    return luminance > 0.5
}

/**
 * Convert a hex or rgb/rgba color string to an rgba string with a given alpha
 */
export const colorToRgba = (color: string, alpha: number): string => {
    if (color.startsWith('#')) {
        const hex = color.replace('#', '')
        let normalizedHex: string

        if (hex.length === 3) {
            normalizedHex = hex
                .split('')
                .map(ch => ch + ch)
                .join('')
        } else if (hex.length === 6) {
            normalizedHex = hex
        } else {
            return color
        }

        const r = parseInt(normalizedHex.substring(0, 2), 16)
        const g = parseInt(normalizedHex.substring(2, 4), 16)
        const b = parseInt(normalizedHex.substring(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    } else if (color.startsWith('rgb')) {
        const rgb = parseRgb(color)
        if (!rgb) return color
        const r = Math.round(rgb[0] * 255)
        const g = Math.round(rgb[1] * 255)
        const b = Math.round(rgb[2] * 255)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
    return color
}
